import { useCallback, useRef } from 'react';
import { equals, findLastIndex, includes, init, isNil, isNotNil, last, omit, pick, prop, propEq, propOr, reject, remove } from 'ramda';
import { buildWidgetBaseline, baselineOccurrences } from '../lib/sessionBaseline';
import { ACTIONS, TARGET_TYPES } from '../../shared/documentHistoriesConstants';

// Two consecutive entries are considered the same functional modification when
// their identity fields match, ignoring the bookkeeping `seq`. This collapses the
// per-keystroke padding spam (updatePadding* fire once per InputNumber change)
// into a single COMPOSITION_UPDATE, while still keeping entries that differ by
// target, section, block, name, etc.
const IDENTITY_KEYS = omit(['seq']);
const isSameModification = (a, b) => equals(IDENTITY_KEYS(a), IDENTITY_KEYS(b));
// Identity of the SLOT an entry targets — never of the widget occupying it, so
// that picking widget A then widget B in one slot still collapses into a single
// addition. rowIndex and widgetIndex are required: without them every slot of a
// section and block looks alike, and two distinct widgets collapse into one
// entry.
const TARGET_KEYS = ['targetType', 'sectionIndex', 'block', 'rowIndex', 'widgetIndex'];
// Identity of a widget PLACEMENT, at the granularity the history renders
// ("Section X - Bloc"). Row and column are excluded on purpose: they shift as
// rows and sections are inserted, removed or reordered during a session, so they
// cannot be trusted when scanning entries pushed earlier.
const PLACEMENT_KEYS = ['widgetId', 'sectionIndex', 'block'];

// A widget arrives in a slot either by being picked from the library or by being
// created there. Both record the arrival that a cancelled deletion undoes.
const ARRIVAL_ACTIONS = [ACTIONS.ADDITION, ACTIONS.CREATION];
// Edits that only make sense while the widget is still placed.
const TWEAK_ACTIONS = [ACTIONS.CONDITIONS_UPDATE, ACTIONS.COMPOSITION_UPDATE];
// Hoisted so the predicates below do not rebuild it for every candidate entry.
const ORPHAN_ACTIONS = [...ARRIVAL_ACTIONS, ...TWEAK_ACTIONS];

const isWidgetDeletion = (entry) =>
  propEq(ACTIONS.DELETION, 'action', entry) && propEq(TARGET_TYPES.WIDGET, 'targetType', entry);

const isSamePlacement = (entry) => (candidate) =>
  propEq(TARGET_TYPES.WIDGET, 'targetType', candidate) &&
  equals(pick(PLACEMENT_KEYS, candidate), pick(PLACEMENT_KEYS, entry));

const isActionIn = (actions) => (candidate) => includes(prop('action', candidate), actions);

const useSessionHistory = () => {
  const stackRef = useRef([]);
  const seqRef = useRef(0);
  // null means "never initialised" and fails open. An empty Map is a legitimate
  // baseline (document opened with no widget placed) and does not.
  const baselineRef = useRef(null);
  // One-shot flag: a structural change (row/column insertion or removal) shifts
  // the rowIndex/widgetIndex of every entry already on the stack, so the next
  // pushEvent cannot trust TARGET_KEYS/isSameModification to compare the
  // incoming entry against `previous`. Consumed by the very next pushEvent,
  // whether or not it actually reaches the collapse block.
  const structuralChangeRef = useRef(false);

  // Occurrence counts held by the persisted document at the start of the session.
  // Set on mount and after every successful save by useDocument. Deliberately
  // untouched by clear(): clearing the stack does not change what is persisted.
  const setBaseline = useCallback((pages) => {
    baselineRef.current = buildWidgetBaseline(pages);
  }, []);

  const markStructuralChange = useCallback(() => {
    structuralChangeRef.current = true;
  }, []);

  const pushEvent = useCallback((rawEntry, meta) => {
    if (isNil(rawEntry)) return;
    const entry = { ...rawEntry, seq: seqRef.current };

    // A widget removal only deserves an entry when it makes the document hold
    // fewer occurrences of that widget than it did at session start. Removing an
    // occurrence that was itself added during the session is a net no-op: drop
    // the deletion and cancel the matching arrival, so the history is not
    // polluted with actions that undo each other.
    // Defaulting occurrencesAfter to 0 keeps callers that pass no metadata on the
    // previous all-or-nothing behaviour.
    if (isWidgetDeletion(entry)) {
      const occurrencesAfter = propOr(0, 'occurrencesAfter', meta);
      const wasPersisted = occurrencesAfter < baselineOccurrences(
        baselineRef.current,
        prop('widgetId', entry),
        prop('sectionIndex', entry),
        prop('block', entry)
      );

      if (!wasPersisted) {
        const matchesPlacement = isSamePlacement(entry);

        if (equals(0, occurrencesAfter)) {
          // Nothing is left of that widget in this section and block: its arrival,
          // its creation and its tweaks all became orphans.
          stackRef.current = reject(
            (candidate) => matchesPlacement(candidate) && isActionIn(ORPHAN_ACTIONS)(candidate),
            stackRef.current
          );
          return;
        }

        // An occurrence survives: cancel one arrival only, since the same widget
        // may legitimately sit in several slots of that section and block. Tweaks
        // are kept, they may describe the occurrence still in place.
        const arrivalIndex = findLastIndex(
          (candidate) => matchesPlacement(candidate) && propEq(ACTIONS.ADDITION, 'action', candidate),
          stackRef.current
        );
        if (arrivalIndex >= 0) stackRef.current = remove(arrivalIndex, 1, stackRef.current);
        return;
      }
    }

    const structuralChanged = structuralChangeRef.current;
    structuralChangeRef.current = false;

    const previous = last(stackRef.current);
    if (isNotNil(previous) && !structuralChanged) {
      if (isSameModification(previous, entry)) return;
      // Only collapse entries carrying the same action on the same slot: picking
      // widget A then widget B in one slot is a single addition, but a deletion
      // followed by an addition there is two distinct facts.
      const isSameTarget = equals(pick(TARGET_KEYS, previous), pick(TARGET_KEYS, entry));
      const isSameAction = propEq(prop('action', previous), 'action', entry);
      if (isSameTarget && isSameAction) stackRef.current = init(stackRef.current);
    }
    stackRef.current = [...stackRef.current, entry];
    seqRef.current += 1;
  }, []);

  const getStack = useCallback(() => [...stackRef.current], []);

  const clear = useCallback(() => {
    stackRef.current = [];
    seqRef.current = 0;
    structuralChangeRef.current = false;
  }, []);

  return {
    pushEvent, getStack, clear, setBaseline, markStructuralChange
  };
};

export default useSessionHistory;
